import { useCallback } from 'react';
import LocateIcon from '@ubnt/icons/LocateIcon';
import OpticalZoomInIcon from '@ubnt/icons/OpticalZoomInIcon';
import OpticalZoomOutIcon from '@ubnt/icons/OpticalZoomOutIcon';
import AddIcon from '@ubnt/icons/AddPrimaryIcon';
import TrashIcon from '@ubnt/icons/TrashIcon';
import FilterIcon from '@ubnt/icons/FilterIcon';
import MapControls from '@ubnt/ui-components/Maps/MapControls/MapControls';
import MapControlsColumn from '@ubnt/ui-components/Maps/MapControls/MapControlsColumn';
import MapControlsGroup from '@ubnt/ui-components/Maps/MapControls/MapControlsGroup';
import MapControlsButton from '@ubnt/ui-components/Maps/MapControls/MapControlsButton';
import Popover from '@ubnt/ui-components/Popover/Popover';
import Tooltip from '@ubnt/link-core-ui/components/Tooltip';
import { clsx } from 'clsx';

import { useDispatch } from 'react-redux';
import { dispatcher } from 'modules/editor/events';
import { useApplicationStore } from 'modules/application/store';
import { trackEvent } from 'utils/googleAnalytics';
import { useSelector } from 'app/store';
import { settingsIsVisibleSelector, settingsToggleVisibility } from 'modules/settings/slice';
import { devicesMeshApExistsSelector } from 'modules/devices/slice';
import { linksAddMeshPoint } from 'modules/links/slice';
import { linksDeleteMeshPoint } from 'modules/links/externalActions';
import SettingsPopoverContent from '../containers/settings/SettingsPopoverContent';

import panelClasses from '../../bottomPanel/components/compositions/DevicePanel.module.scss';
import classes from './Tools.module.scss';

interface Props {
  className: string;
}

export function Tools(props: Props) {
  const dispatch = useDispatch();
  const onZoomIn = useCallback(() => {
    dispatcher.zoomIn();
    trackEvent('toolsSelect', { tool: 'zoomIn' });
  }, []);
  const onZoomOut = useCallback(() => {
    dispatcher.zoomOut();
    trackEvent('toolsSelect', { tool: 'zoomOut' });
  }, []);
  const onFitScene = useCallback(() => {
    dispatcher.fitScene();
    trackEvent('toolsSelect', { tool: 'fitScene' });
  }, []);
  const onToggleClick = useCallback(() => {
    trackEvent('settingsSelect');
    dispatch(settingsToggleVisibility());
  }, [dispatch]);
  const onAddMeshClick = useCallback(() => {
    dispatch(linksAddMeshPoint());
    trackEvent('toolsAddMesh');
  }, [dispatch]);
  const onDeleteMeshClick = useCallback(() => {
    dispatch(linksDeleteMeshPoint());
    trackEvent('toolsDeleteMesh');
  }, [dispatch]);

  const isMobile = useApplicationStore((state) => state.isMobile);
  const isSettingsVisible = useSelector(settingsIsVisibleSelector);
  const meshApExists = useSelector(devicesMeshApExistsSelector);

  const settingsToggle = useCallback(
    () => (
      <Tooltip position="bottom" description="Display Options" key="4" disableHover={isMobile}>
        <MapControlsButton
          aria-label="Display Options"
          className={classes.mapControlsButton}
          data-testid="display-options"
          Icon={FilterIcon}
          size="small"
          onClick={onToggleClick}
        />
      </Tooltip>
    ),
    [isMobile, onToggleClick]
  );

  // 'key=' on tooltip fixes size issue!
  return (
    <MapControls>
      <MapControlsColumn align="left" className={clsx(classes.rowColumn, props.className)} direction="top">
        <MapControlsGroup className={classes.textButtonGroup}>
          <MapControlsButton
            aria-label={meshApExists ? 'Delete Mesh' : 'Add Mesh'}
            className={classes.mapControlsButton}
            data-testid="mesh-toggle"
            Icon={meshApExists ? TrashIcon : AddIcon}
            size="small"
            onClick={meshApExists ? onDeleteMeshClick : onAddMeshClick}
          >
            <span>{meshApExists ? 'Delete Mesh' : 'Add Mesh'}</span>
          </MapControlsButton>
        </MapControlsGroup>
        <MapControlsGroup className={classes.rowGroup}>
          {!isMobile && (
            <>
              <Tooltip position="bottom" description="Zoom In" key="1">
                <MapControlsButton
                  aria-label="Zoom In"
                  className={classes.mapControlsButton}
                  data-testid="zoom-in"
                  Icon={OpticalZoomInIcon}
                  size="small"
                  onClick={onZoomIn}
                />
              </Tooltip>
              <Tooltip position="bottom" description="Zoom Out" key="2">
                <MapControlsButton
                  aria-label="Zoom Out"
                  className={classes.mapControlsButton}
                  data-testid="zoom-out"
                  Icon={OpticalZoomOutIcon}
                  size="small"
                  onClick={onZoomOut}
                />
              </Tooltip>
            </>
          )}
          <Tooltip position="bottom" description="Center" key="3">
            <MapControlsButton
              aria-label="Center"
              className={classes.mapControlsButton}
              data-testid="center-canvas"
              Icon={LocateIcon}
              size="small"
              onClick={onFitScene}
            />
          </Tooltip>
        </MapControlsGroup>
        <MapControlsGroup>
          <Popover
            open={isMobile ? isSettingsVisible : undefined}
            align="rightBottom"
            renderCard={SettingsPopoverContent}
            renderToggle={settingsToggle}
            classNameCardWrap={panelClasses.popoverWrap}
          />
        </MapControlsGroup>
      </MapControlsColumn>
    </MapControls>
  );
}
